import { P, match } from '@frontend/duck-tape';
import { useEffect, useState } from '@frontend/react';
import { concatClasses, safeCss } from '@frontend/web-utils';
import { Dialog } from '@mantine/core';
import { YStack } from '../Primitives';
import { Text } from '../Primitives/Texts/Text';

const isLocalUrl = (url: string) => url.includes('localhost') || url.includes('127.0.0.1');

const useLocalApiReachable = (apiUrl: string) => {
  const [isReachable, setIsReachable] = useState<boolean | 'loading'>(isLocalUrl(apiUrl) ? 'loading' : false);

  useEffect(() => {
    if (!isLocalUrl(apiUrl)) {
      return;
    }

    const controller = new AbortController();
    setIsReachable('loading');

    fetch(apiUrl, { method: 'HEAD', signal: AbortSignal.any([controller.signal, AbortSignal.timeout(3000)]) })
      .then(() => setIsReachable(true))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
        setIsReachable(false);
      });

    return () => controller.abort();
  }, [apiUrl]);

  return isReachable;
};

export type EnvironmentDialogProps = {
  apiUrl: string;
  env: EnvType;
};

export const EnvironmentDialog = ({ apiUrl, env }: EnvironmentDialogProps) => {
  const [opened, setOpened] = useState(true);
  const localApiReachable = useLocalApiReachable(apiUrl);

  const bgClassName = match<EnvType>(env)
    .with('development', () => safeCss('bg-blue-600'))
    .with('local', () => safeCss('bg-green-600'))
    .with('staging', () => safeCss('bg-yellow-500'))
    // Should never get here
    .with('production', () => '')
    .exhaustive();

  const { api, textClassName } = match(apiUrl)
    .with(P.string.includes('dev'), () => ({ api: 'development', textClassName: safeCss('text-blue-600') }))
    .with(P.string.includes('staging'), () => ({ api: 'staging', textClassName: safeCss('text-yellow-500') }))
    .with(P.when(isLocalUrl), () =>
      match(localApiReachable)
        .with(true, () => ({ api: 'local', textClassName: safeCss('text-green-600') }))
        .with('loading', () => ({ api: 'local (checking...)', textClassName: safeCss('text-green-600') }))
        .with(false, () => ({ api: 'invalid', textClassName: '' }))
        .exhaustive(),
    )
    .otherwise(() => ({ api: 'invalid', textClassName: '' }));

  if (!env || env === 'production') {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }
  return (
    <Dialog
      className="border-1 border-borderPrimary gap-x-md flex flex-row items-center"
      onClose={() => setOpened(false)}
      opened={opened}
      radius="md"
      shadow="xl"
      withCloseButton
    >
      <YStack className="mr-lg">
        <YStack className={concatClasses('w-md h-md rounded-lg', bgClassName)} />
      </YStack>
      <Text type="h6">
        You are on the {env} environment, pointing to the{' '}
        <Text className={textClassName} span type="h6">
          {api}
        </Text>{' '}
        API
      </Text>
    </Dialog>
  );
};
